import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import CmsListingsCollection from '@reverbdotcom/commons/src/cms/components/cms_listings_collection';
import { DEALS_AND_STEALS_CURATED_SET_ID } from '@reverbdotcom/commons/src/constants';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { HomepageDealsAndStealsListingsQuery, reverb_search_ListingsSearchRequest_PriceValue } from '@reverbdotcom/commons/src/rql';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import I18n from 'i18n-js';
import React from 'react';
import URLHelpers from './shared/url_helpers';

interface ExternalProps {
  mpid?: string;
}

type Props = ChildProps<ExternalProps, HomepageDealsAndStealsListingsQuery.Query>;

export function HomepageDealsAndStealsPersonalizedListings(props: Props) {
  const { data } = props;

  const topCategoryUuids = data?.me?.profile?.topCategoryUuids || [];
  const topCategories = (data?.me?.profile?.topCategories || []).reduce((memo, slug, idx) => {
    const uuid = topCategoryUuids[idx];
    const [product_type, category] = slug.split('/');
    memo[uuid] = {
      categoryName: I18n.t(`discovery.taxonomy.categories.${uuid}`),
      category,
      slug,
      product_type,
    };
    return memo;
  }, { 'ALL': { categoryName: I18n.t('discovery.taxonomy.allCategories') } });

  const eligible = !data.loading && topCategoryUuids.length > 0;

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: 'HomepageDealsAndStealsListingsCollection',
  }, eligible);

  const topAffinities = topCategoryUuids.slice(0, 3);

  const categoryInfo = topCategories[topAffinities[0]];
  const dealsAndStealsPath = URLHelpers.marketplacePath({
    product_type: categoryInfo?.product_type,
    category: categoryInfo?.category,
    deals_and_steals: true,
  });

  return (
    <CmsListingsCollection
      data-deals-and-steals-listings
      curatedSetId={DEALS_AND_STEALS_CURATED_SET_ID}
      componentName="HomepageDealsAndSteals"
      trackingName="HomepageDealsAndSteals"
      ctaTargetHref={dealsAndStealsPath}
      categoryUuids={topAffinities}
      priceValueV2={reverb_search_ListingsSearchRequest_PriceValue.GOOD}
      title={I18n.t('discovery.homepage.dealsAndStealsForYou')}
      maxCount={6}
      largeTiles={false}
      ssr={false}
    />
  );
}

const withQuery = withGraphql<Props, HomepageDealsAndStealsListingsQuery.Query>(
  gql`
    query Homepage_Deals_And_Steals_Listings_Query(
      $mpid: String
    ) {
      me {
        uuid
        profile(input: {
          mpid: $mpid
        }) {
          topCategories
          topCategoryUuids
        }
      }
    }
  `,
  {
    options: (props) => {
      return {
        ssr: false,
        variables: {
          mpid: props.mpid,
        },
      };
    },
  },
);

export const HomepageDealsAndStealsPersonalizedListingsCollection = withQuery(HomepageDealsAndStealsPersonalizedListings);
